import { ContactoPersona, ContactoPersonaRequest } from '../../model/persona/contacto-persona';
import { CursorPageResponse } from '../../model/cumplimiento/cumplimiento';
import { DireccionCursor } from '../../model/common/page-response.dto';

/**
 * Puerto Abstracto: ContactoPersonaRepository
 * 
 * Define el contrato que deben implementar los adaptadores de infraestructura.
 * Trabaja con la entidad de dominio ContactoPersona.
 * 
 * NOTA: Solo aplica para personas jurídicas (NIT)
 * 
 * Arquitectura Hexagonal:
 * - Este puerto está en el dominio
 * - HttpContactoPersonaRepository (adaptador) lo implementa en infraestructura
 * 
 * @author Emerson Rodriguez - Ingeneo
 */
export abstract class ContactoPersonaRepository {
  
  /**
   * Obtiene UNA página de contactos de una persona (keyset/cursor en servidor).
   * Legacy: PersonaFacade.consultarContactoPersona (start/length en servidor).
   *
   * @param soloActivos true → solo snactivo='S'
   * @param cursor null para la primera página; nextCursor/previousCursor de la página actual
   * @param size tamaño de página (máx. 100)
   * @param direction NEXT | PREVIOUS | LAST (LAST con cursor vacío = última página)
   */
  abstract getByPersonaPaged(
    cdpersona: number,
    soloActivos: boolean,
    cursor: string | null,
    size: number,
    direction?: DireccionCursor
  ): Promise<CursorPageResponse<ContactoPersona>>;
  
  /**
   * Crea un nuevo contacto
   */
  abstract create(cdpersona: number, contacto: ContactoPersonaRequest): Promise<ContactoPersona>;
  
  /**
   * Actualiza un contacto existente
   */
  abstract update(cdpersona: number, nmconsecutivo: number, contacto: ContactoPersonaRequest): Promise<ContactoPersona>;
  
  /**
   * Inactiva un contacto (soft delete).
   * limpiarRolCargo=true desde el popup de la orden (legacy ListaContactosViewImpl anula CDROL y
   * CDCARGO); false desde Administración de contactos (AdminContactosViewImpl).
   */
  abstract delete(cdpersona: number, nmconsecutivo: number, limpiarRolCargo?: boolean): Promise<void>;
  
  
  /**
   * Obtiene contactos paginados de un centro de trabajo.
   * Retorna items + total para soportar paginación server-side.
   */
  abstract getByCentroTrabajoPaged(
    nmconsecCt: number,
    cursor: string | null,
    size: number,
    direction?: DireccionCursor
  ): Promise<CursorPageResponse<ContactoPersona>>;
  
  /**
   * Inactiva un contacto desde el popup de la orden con centro de trabajo.
   * Legacy ListaContactosViewImpl: SNACTIVO='N', FEBAJA y CDROL/CDCARGO en null en
   * TPSC_PERSONAS_CONTACTOS; el vínculo en TPSC_CONTAC_CENTRO_TRAB no se modifica.
   * 
   * @param cdpersona Código de la persona
   * @param nmconsecutivo Consecutivo del contacto
   * @param nmconsecCentroTrabajo Consecutivo del centro de trabajo
   */
  abstract deleteWithCentroTrabajo(
    cdpersona: number, 
    nmconsecutivo: number, 
    nmconsecCentroTrabajo: number
  ): Promise<void>;

  /**
   * Crea un contacto a partir de una orden de servicio.
   * El backend resuelve cdpersona y nmconsecCentroTrabajo automáticamente desde la orden.
   * Migrado del legacy: AdminContactosHandler.crearContactoCentroTrab() que recibe ordenServicioDetalleDto.
   * 
   * @param nmconsecOrden Consecutivo del detalle de orden de servicio
   * @param contacto Datos del contacto a crear
   */
  abstract createFromOrden(nmconsecOrden: number, contacto: ContactoPersonaRequest): Promise<ContactoPersona>;

  /**
   * Devuelve el contacto de la orden con el correo indicado o lo crea si no existe.
   * Legacy: CreateOrUpdateCumplimientoActivity.crearNuevoContacto (cumplimiento sin programación).
   */
  abstract resolverDesdeOrden(nmconsecOrden: number, contacto: ContactoPersonaRequest): Promise<ContactoPersona>;

  /**
   * Devuelve el contacto del centro de trabajo con el correo indicado o lo crea si no existe.
   * Legacy: CreateOrUpdateCumplimientoActivity.crearNuevoContacto en el cumplimiento sin OC, donde la orden
   * aún no tiene consecutivo y el contacto se resuelve con el centro de trabajo que dejó SP_CREAR_BENEF_CT.
   *
   * @param cdpersona Persona (beneficiario) dueña del centro de trabajo
   * @param nmconsecCentroTrabajo Consecutivo del centro de trabajo
   */
  abstract resolverDesdeCentroTrabajo(
    cdpersona: number,
    nmconsecCentroTrabajo: number,
    contacto: ContactoPersonaRequest
  ): Promise<ContactoPersona>;

  /**
   * Obtiene UNA página de contactos de una orden de servicio (keyset/cursor en servidor).
   * Legacy: AdminContactosHandler.listarContactosBenefOrden — contactos del centro de trabajo de la
   * orden o, si no tiene, los del beneficiario.
   */
  abstract getByOrdenPaged(
    nmconsecOrden: number,
    cursor: string | null,
    size: number,
    direction?: DireccionCursor
  ): Promise<CursorPageResponse<ContactoPersona>>;
}
